import { useStyletron } from "baseui";
import { Block } from "baseui/block";
import { Button } from "baseui/button";
import { Card, StyledAction, StyledBody } from "baseui/card";
import { ANCHOR, Drawer, SIZE } from "baseui/drawer";
import { FlexGrid, FlexGridItem } from 'baseui/flex-grid';
import { FormControl } from 'baseui/form-control';
import { ChevronLeft, ChevronRight, Delete } from "baseui/icon";
import { HeadingXLarge, ParagraphMedium } from 'baseui/typography';
import { forwardRef, useEffect, useRef, useState } from "react";
import { useDispatch } from 'react-redux';
import { submitChallenge, uploadChallengePhotos, deleteChallengePhoto } from "../application/actions/challenges";
import { routeReplace, routeTo } from "../application/actions/ui";
import create_solution from '../assets/images/create_solution.png';
import useBreakpoint from "../hooks/useBreakpoint";
import useLocale from "../hooks/useLocale";
import useFormBuffer from "../hooks/useFormBuffer";
import ReDoBanner from "./ReDoBanner";
import ReDoButton from "./ReDoButton";
import ReDoFileUpload from "./ReDoFileUpload";
import ReDoInterests from "./ReDoInterests";
import ReDoProgressBar from "./ReDoProgressBar";
import ReDoSpinner from "./ReDoSpinner";
import ReDoTextarea from "./RedoTextarea";
import { DURATION, useSnackbar } from "baseui/snackbar";



const ReDoCreateChallenge = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const [css, theme] = useStyletron();
  const breakpoint = useBreakpoint()

  const { enqueue } = useSnackbar();
  const { t } = useLocale();
  const { readBuffer, writeBuffer, clearBuffer } = useFormBuffer('createChallenge');

  const stages = [
    { label: t('createChallenge.stage_title'),        curr: 1, max: 8 },
    { label: t('createChallenge.stage_problem'),      curr: 2, max: 8 },
    { label: t('createChallenge.stage_target_group'), curr: 3, max: 8 },
    { label: t('createChallenge.stage_goal'),         curr: 4, max: 8 },
    { label: t('createChallenge.stage_time'),         curr: 5, max: 8 },
    { label: t('createChallenge.stage_incentives'),   curr: 6, max: 8 },
    { label: t('createChallenge.stage_why'),          curr: 7, max: 8 },
    { label: t('createChallenge.stage_visuals'),      curr: 8, max: 8 },
  ]

  const time = [
    {
      group: '',
      list: [
        { label: t('createChallenge.time_short_label'),  description: t('createChallenge.time_short_desc'),  value: 'short' },
        { label: t('createChallenge.time_medium_label'), description: t('createChallenge.time_medium_desc'), value: 'medium' },
        { label: t('createChallenge.time_long_label'),   description: t('createChallenge.time_long_desc'),   value: 'long' },
      ],
      color: '#fff'
    }
  ]

  const rawSaved = props.challenge?.id ? null : readBuffer();
  const isTaintedSaved = !!rawSaved?.payload?.id;
  const saved = isTaintedSaved ? null : rawSaved;
  const lastStage = stages[stages.length - 1].curr;
  const [stageCurr, setStageCurr] = useState(props.challenge?.id ? lastStage : (saved ? saved.stage : 1))
  const stage = stages.find(s => s.curr === stageCurr) || stages[0]
  const [payload, setPayload] = useState(saved?.payload || props.challenge || {})
  const [photos, setPhotos] = useState([])
  const [removedPhotos, setRemovedPhotos] = useState([])
  const [isOpen, setIsOpen] = useState(false);

  const [loading, setLoading] = useState(false)

  const scrollable = useRef()

  useEffect(() => {
    if (isTaintedSaved) clearBuffer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!props.challenge?.id) {
      writeBuffer(stageCurr, payload);
    }
  }, [stageCurr, payload, writeBuffer, props.challenge?.id])

  const column = css({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    justifyContent: 'flex-start',
    marginTop: '7ch',
    gap: '3ch'
  });

  const onNext = (e) => {
    e.preventDefault();
    scrollable.current.scrollTo(0, 0)

    const data = new FormData(e.target);
    const stepPayload = {}

    for (const pair of data.entries()) {
      stepPayload[pair[0]] = pair[1]
    }

    const finalPayload = { ...payload, ...stepPayload }

    setPayload(finalPayload)

    const currStage = stages.findIndex(s => s.curr === stageCurr)
    if (currStage < stages.length - 1) {
      setStageCurr(stages[currStage + 1].curr)
    } else {
      setLoading(true)
      dispatch(submitChallenge({ ...finalPayload, period: finalPayload.period?.toLowerCase() })).then(async res => {
        let challenge = res
        if (props.challenge?.id && removedPhotos.length) {
          const deletedIds = new Set()
          for (const photo of removedPhotos) {
            try {
              await dispatch(deleteChallengePhoto(props.challenge.id, photo.id))
              deletedIds.add(photo.id)
            } catch (e) {
              // non-fatal: continue even if delete fails
            }
          }
          if (deletedIds.size > 0) {
            challenge = { ...challenge, photos: (challenge.photos || []).filter(p => !deletedIds.has(p.id)) }
          }
        }
        if (photos.length) {
          for (const photo of photos) {
            try {
              const photoRes = await dispatch(uploadChallengePhotos({ challenge: challenge.id, title: photo.file?.file?.name ?? photo.file?.name ?? photo.title, photo: photo.photo }))
              challenge = photoRes
            } catch (e) {
              setLoading(false)
              enqueue({ message: e.message + ' ' + e.response?.data?.error, dismissAfter: DURATION.shortest, startEnhancer: () => <Delete size={theme.sizing.scale800} /> });
            }
          }
        }
        setLoading(false)
        if (!props.challenge?.id) clearBuffer()
        const navigate = props.challenge?.id ? routeReplace : routeTo
        dispatch(navigate({ view: 'challenge', payload: { challenge } }))
      }).catch(e => {
        setLoading(false)
        enqueue({ message: e.response.data.error, dismissAfter: DURATION.shortest, startEnhancer: () => <Delete size={theme.sizing.scale800} /> })
      })
    }
  }


  const onPrev = (e) => {
    e.preventDefault();
    scrollable.current && scrollable.current.scrollTo(0, 0)

    const formEl = scrollable.current instanceof HTMLFormElement
      ? scrollable.current
      : scrollable.current?.querySelector('form');

    const currStage = stages.findIndex(s => s.curr === stageCurr)
    if (!formEl) {
      if (currStage > 0) setStageCurr(stages[currStage - 1].curr)
      return;
    }

    const data = new FormData(formEl);
    const stepPayload = {}

    for (const pair of data.entries()) {
      stepPayload[pair[0]] = pair[1]
    }

    const updated = { ...payload, ...stepPayload }
    setPayload(updated)

    if (currStage > 0) {
      setStageCurr(stages[currStage - 1].curr)
    }
  }

  const onUpload = (e) => {
    if (!Array.isArray(e)) return;
    setPhotos(e.filter(f => f.file));
    const remainingServerPhotos = e
      .filter(f => !f.file)
      .map(({ preview, ...rest }) => rest);
    setPayload(prev => ({ ...prev, photos: remainingServerPhotos }));
    if (props.challenge?.id) {
      const originalPhotos = props.challenge.photos || [];
      setRemovedPhotos(originalPhotos.filter(orig =>
        !remainingServerPhotos.some(rem => rem.photo === orig.photo)
      ));
    }
  };



  if (breakpoint === 'large') {
    return (
      <>
        {loading && <ReDoSpinner />}
        <FlexGrid
          flexGridColumnCount={[1, 1, 1, 2]}
          flexGridRowGap="scale800"
          height='100%'
          alignItems='center'
          overflow='hidden'
          margin='0 -28px'
        >

          <FlexGridItem position='relative' height='100%' display='flex'>
            <ReDoBanner align>
              <div className={css({ height: '100%', justifyContent: 'space-between', display: 'flex', flexDirection: 'column', padding: `${theme.sizing.scale1600} ${theme.sizing.scale3200}` })}>
                <div className={css({ paddingTop: theme.sizing.scale2400 })}>
                  <HeadingXLarge overrides={{ Block: { style: { lineHeight: '48px', fontWeight: '300' } } }} width='230px'>{t('createChallenge.heading')}</HeadingXLarge>
                </div>
                <Block display='flex' flexDirection='column' marginTop='auto'>
                  <ReDoProgressBar label={stage.label} value={stage.curr} maxValue={stage.max} />
                </Block>
              </div>
            </ReDoBanner>
          </FlexGridItem>

          <FlexGridItem height='100%' overflow='hidden'>
            <Block position='relative' height='calc(100% - 70px)' marginTop='70px'>
                <Block overrides={{ Block: { style: { zIndex: '1', pointerEvents: 'none' } } }} position='absolute' top='0px' left='0' width='100%' height='50px' backgroundImage='linear-gradient(rgb(243,242,238) 0%, rgba(255, 255, 255, 0) 100%)' />

                <form ref={scrollable} className={css({ height: '100%', overflow: 'auto' })} onSubmit={onNext}>
                  <div className={css({ paddingTop: '20px', maxWidth: '375px', margin: 'auto', paddingBottom: '28px', alignItems: 'center', display: 'flex', flexDirection: 'column', minHeight: '100%', paddingRight: '1ch' })}>

                    {stage.curr === 1 &&
                      <>
                        <Block width='100%' marginTop='5%'>
                          <FormControl>
                            <ReDoTextarea
                              label={t('createChallenge.title_label')}
                              placeholder={t('createChallenge.title_placeholder')}
                              name='title'
                              required
                              value={payload.title}
                              maxLength={20}
                            />
                          </FormControl>
                        </Block>
                        <Block width='100%' marginTop='5%'>
                          <FormControl>
                            <ReDoTextarea
                              label={t('createChallenge.summary_label')}
                              placeholder={t('createChallenge.summary_placeholder')}
                              maxLength={500}
                              name='summary'
                              required
                              value={payload.summary}
                            />
                          </FormControl>
                        </Block>
                      </>
                    }

                    {stage.curr === 2 &&
                      <>
                        <Block width='100%' marginTop='5%'>
                          <FormControl>
                            <ReDoTextarea
                              label={t('createChallenge.problem_label')}
                              placeholder={t('createChallenge.type_here')}
                              maxLength={500}
                              name='problem'
                              required
                              value={payload.problem}
                            />
                          </FormControl>
                        </Block>
                      </>
                    }

                    {stage.curr === 3 &&
                      <>
                        <Block width='100%' marginTop='5%'>
                          <FormControl>
                            <ReDoTextarea
                              label={t('createChallenge.target_label')}
                              id="target-input"
                              placeholder={t('createChallenge.type_here')}
                              name='target'
                              required
                              value={payload.target}
                              maxLength={500}
                            />
                          </FormControl>
                        </Block>
                      </>
                    }

                    {stage.curr === 4 &&
                      <>
                        <Block width='100%' marginTop='5%'>
                          <FormControl>
                            <ReDoTextarea
                              label={t('createChallenge.goal_label')}
                              placeholder={t('createChallenge.type_here')}
                              maxLength={250}
                              name='goal'
                              required
                              value={payload.goal}
                            />
                          </FormControl>
                        </Block>
                      </>
                    }

                    {stage.curr === 5 &&
                      <>
                        <Block width='100%' marginTop='5%'>
                          <FormControl>
                            <ReDoInterests
                              label={t('createChallenge.time_label')}
                              options={time}
                              values={payload?.period}
                              name='period'
                              radio
                            />
                          </FormControl>
                        </Block>
                      </>
                    }

                    {stage.curr === 6 &&
                      <>
                        <Block width='100%' marginTop='5%'>
                          <FormControl>
                            <ReDoTextarea
                              label={t('createChallenge.incentives_label')}
                              placeholder={t('createChallenge.type_here')}
                              maxLength={250}
                              name='incentives'
                              required
                              value={payload.incentives}
                            />
                          </FormControl>
                        </Block>
                        <Block width='100%' marginTop='5%'>
                          <FormControl>
                            <ReDoTextarea
                              label={t('createChallenge.legal_label')}
                              placeholder={t('createChallenge.legal_placeholder')}
                              name='agbUrl'
                              value={payload.agbUrl}
                              maxLength={40}
                              link
                            />
                          </FormControl>
                        </Block>
                      </>
                    }

                    {stage.curr === 7 &&
                      <>
                        <Block width='100%' marginTop='5%'>
                          <FormControl>
                            <ReDoTextarea
                              label={t('createChallenge.why_label')}
                              placeholder={t('createChallenge.type_here')}
                              maxLength={250}
                              name='why'
                              required
                              value={payload.why}
                            />
                          </FormControl>
                        </Block>
                      </>
                    }

                    {stage.curr === 8 &&
                      <>
                        <Block width='100%' marginTop='5%'>
                          <FormControl>
                            <ReDoFileUpload
                              multiple
                              maxCount={3}
                              onUpload={onUpload}
                              onRemove={onUpload}
                              label={t('createChallenge.visuals_label')}
                              values={payload?.photos?.map(f => ({ ...f, preview: f.photo }))}
                            />
                          </FormControl>
                        </Block>
                      </>
                    }

                    <Block paddingBottom='50px' justifyContent='space-between' display='flex' width='100%' marginTop='5%'>
                      {stage.curr !== 1 &&
                        <ReDoButton
                          type='button'
                          onClick={onPrev}
                          kind='inverse'
                          overrides={{
                            BaseButton: {
                              style: {
                                marginTop: '0',
                                marginBottom: '0',
                                paddingLeft: '0 !important',
                                paddingTop: '0 !important',
                                paddingBottom: '0 !important',
                                backgroundColor: 'transparent',
                                color: theme.colors.black,
                                width: 'auto',
                                fontWeight: 'bold'
                              }
                            },
                            StartEnhancer: {
                              style: {
                                marginRight: '0'
                              }
                            }
                          }}
                          startEnhancer={() => <ChevronLeft size={theme.sizing.scale1000} />}
                        >
                          {t('common.back')}
                        </ReDoButton>
                      }
                      <ReDoButton
                        type='submit'
                        kind='brand'
                        overrides={{
                          BaseButton: {
                            style: {
                              marginBottom: '0',
                              paddingTop: '5px',
                              paddingBottom: '5px',
                              paddingRight: '0',
                              width: 'auto',
                              marginLeft: 'auto'
                            }
                          },
                          EndEnhancer: {
                            style: {
                              marginLeft: '0'
                            }
                          }
                        }}
                        endEnhancer={() => <ChevronRight size={theme.sizing.scale1000} />}
                      >
                        {stage.curr !== 8 ? t('common.next') : t('common.submit')}
                      </ReDoButton>
                    </Block>

                  </div>
                </form>

                <Block overrides={{ Block: { style: { pointerEvents: 'none' } } }} position='absolute' bottom='0' left='0' width='100%' height='50px' backgroundImage='linear-gradient(rgba(255, 255, 255, 0) 0%, rgb(243,242,238) 100%)' />

              </Block>
          </FlexGridItem>
        </FlexGrid>
      </>
    );
  } else {
    return (
      <>

        <Card
          title={t('createChallenge.mobile_card_title')}
          overrides={{
            Root: {
              style: {
                height: '75%', border: 'none', borderRadius: '25px !important',
                backgroundImage: `url(${create_solution})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                margin: 'auto 0'
              }
            },
            Title: {
              style: {
                color: 'white',
                fontSize: '2rem',
                fontWeight: 'normal',
              }
            },
            Contents: {
              style: {
                height: '100%',
                margin: '0',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                padding: '2ch'
              }
            }
          }}
        >
          <StyledBody>
            <ParagraphMedium color='white'>
              {t('createChallenge.mobile_card_text')}
            </ParagraphMedium>
          </StyledBody>
          <StyledAction>
            <Block display='flex' alignItems='center' justifyContent='flex-end'>
              <Button onClick={() => setIsOpen(true)} shape="pill" overrides={{ BaseButton: { style: { fontWeight: 'bold', color: 'black', backgroundColor: 'white', minWidth: '30%', padding: '1ch 2ch !important', ':hover': { backgroundColor: theme.colors.secondary200 } } } }}>
                {t('createChallenge.mobile_upload_btn')}
              </Button>
            </Block>
          </StyledAction>
        </Card>
        <Drawer
          isOpen={isOpen}
          autoFocus
          onClose={() => {
            setIsOpen(false)
          }}
          anchor={ANCHOR.bottom}
          size={SIZE.default}
          showBackdrop={true}
          overrides={{
            DrawerContainer: {
              style: {
                borderTopRightRadius: '32px',
                borderTopLeftRadius: '32px',
                height: 'calc(100vh - var(--redo-nav-bar-height, 68px) - 120px)',
                maxHeight: 'calc(100vh - var(--redo-nav-bar-height, 68px) - 120px)'
              }
            },
            DrawerBody: {
              style: {
                margin: '0 !important',
                padding: '0',
                overflow: 'hidden'
              }
            },
            Close: {
              style: {
                color: theme.colors.primary300,
                width: '48px',
                height: '48px',
                background: 'white'
              }
            }
          }}
        >
          {loading && <ReDoSpinner />}
          <div ref={scrollable} className={`${column} ${css({ paddingTop: '3ch', overflow: 'auto' })}`}>

            <Block padding='0 3ch' justifyContent='space-between' display='flex' flexDirection='column'>
              <Block alignSelf='flex-end' justifyContent='space-between' display='flex'>
                <ReDoProgressBar kind='text' label={stage.label} value={stage.curr} maxValue={stage.max} />
              </Block>

              <HeadingXLarge paddingTop='1ch' margin='0'>{stage.label}</HeadingXLarge>
            </Block>

            <Block>
              <form className={css({ padding: '0 3ch', alignItems: 'center', display: 'flex', flexDirection: 'column' })} onSubmit={onNext}>
                <div className={css({ width: '100%', maxWidth: '375px', paddingBottom: '10ch' })}>
                  {stage.curr === 1 &&
                    <>
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoTextarea
                            label={t('createChallenge.title_label')}
                            placeholder={t('createChallenge.title_placeholder')}
                            name='title'
                            required
                            value={payload.title}
                            maxLength={20}
                            shadow
                          />
                        </FormControl>
                      </Block>
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoTextarea
                            label={t('createChallenge.summary_label')}
                            placeholder={t('createChallenge.summary_placeholder')}
                            maxLength={500}
                            name='summary'
                            required
                            value={payload.summary}
                            shadow
                          />
                        </FormControl>
                      </Block>
                    </>
                  }
                  {stage.curr === 2 &&
                    <>
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoTextarea
                            label={t('createChallenge.problem_label')}
                            placeholder={t('createChallenge.type_here')}
                            maxLength={500}
                            name='problem'
                            required
                            value={payload.problem}
                            shadow
                          />
                        </FormControl>
                      </Block>
                    </>
                  }
                  {stage.curr === 3 &&
                    <>
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoTextarea
                            label={t('createChallenge.target_label')}
                            id="target-input"
                            placeholder={t('createChallenge.type_here')}
                            name='target'
                            required
                            value={payload.target}
                            maxLength={500}
                            shadow
                          />
                        </FormControl>
                      </Block>
                    </>
                  }
                  {stage.curr === 4 &&
                    <>
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoTextarea
                            label={t('createChallenge.goal_label')}
                            placeholder={t('createChallenge.type_here')}
                            maxLength={250}
                            name='goal'
                            required
                            value={payload.goal}
                            shadow
                          />
                        </FormControl>
                      </Block>
                    </>
                  }
                  {stage.curr === 5 &&
                    <>
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoInterests
                            label={t('createChallenge.time_label')}
                            options={time}
                            values={payload?.period}
                            name='period'
                            radio
                            invert
                          />
                        </FormControl>
                      </Block>
                    </>
                  }
                  {stage.curr === 6 &&
                    <>
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoTextarea
                            label={t('createChallenge.incentives_label')}
                            placeholder={t('createChallenge.type_here')}
                            maxLength={250}
                            name='incentives'
                            required
                            value={payload.incentives}
                            shadow
                          />
                        </FormControl>
                      </Block>
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoTextarea
                            label={t('createChallenge.legal_label')}
                            placeholder={t('createChallenge.legal_placeholder')}
                            name='agbUrl'
                            value={payload.agbUrl}
                            maxLength={40}
                            link
                          />
                        </FormControl>
                      </Block>
                    </>
                  }
                  {stage.curr === 7 &&
                    <>
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoTextarea
                            label={t('createChallenge.why_label')}
                            placeholder={t('createChallenge.type_here')}
                            maxLength={250}
                            name='why'
                            required
                            value={payload.why}
                            shadow
                          />
                        </FormControl>
                      </Block>
                    </>
                  }
                  {stage.curr === 8 &&
                    <>
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoFileUpload
                            multiple
                            maxCount={3}
                            onUpload={onUpload}
                            onRemove={onUpload}
                            label={t('createChallenge.visuals_label')}
                            values={payload?.photos?.map(f => ({ ...f, preview: f.photo }))}
                          />
                        </FormControl>
                      </Block>
                    </>
                  }
                  <Block justifyContent='space-between' display='flex' width='100%' marginTop='5%'>
                    {stage.curr !== 1 &&
                      <ReDoButton
                        type='button'
                        onClick={onPrev}
                        kind='inverse'
                        overrides={{
                          BaseButton: {
                            style: {
                              marginTop: '0',
                              marginBottom: '0',
                              paddingLeft: '0 !important',
                              paddingTop: '0 !important',
                              paddingBottom: '0 !important',
                              backgroundColor: 'transparent',
                              color: theme.colors.black,
                              width: 'auto'
                            }
                          }
                        }}
                        startEnhancer={() => <ChevronLeft size={theme.sizing.scale1000} />}
                      >
                        {t('common.back')}
                      </ReDoButton>
                    }
                    <ReDoButton
                      type='submit'
                      kind='brand'
                      overrides={{
                        BaseButton: {
                          style: {
                            marginBottom: '0',
                            paddingTop: '5px',
                            paddingBottom: '5px',
                            paddingRight: '0',
                            width: 'auto',
                            marginLeft: 'auto'
                          }
                        }
                      }}
                      endEnhancer={() => <ChevronRight size={theme.sizing.scale1000} />}
                    >
                      {stage.curr !== 8 ? t('common.next') : t('common.submit')}
                    </ReDoButton>
                  </Block>
                </div>
              </form>
            </Block>

          </div >
        </Drawer >
      </>
    )
  }
});

export default ReDoCreateChallenge;